import { useEffect, useMemo, useState } from "react";
import {
  IconChevronLeft,
  IconChevronRight,
  IconCircleCheck,
  IconCircleX,
  IconEye,
  IconFlagCheck,
  IconSearch,
  IconShoppingCart,
  IconUserCircle,
  IconX,
  IconRocketOff,
} from "@tabler/icons-react";
import CmsLayout from "@layouts/CmsLayout";
import PurchaseCompletionModal from "@components/cms_purchases/PurchaseCompletionModal";
import useCmsPurchases from "@hooks/cmsPurchases/useCmsPurchases";
import { formatBackendDate } from "@utils/formatBackendDate";
import "@assets/css/cms_purchases/panel.css";

const pageLimit = 20;

function formatMoney(purchase) {
  const amount = Number(purchase?.totalAmount || 0);
  const currency = purchase?.currencyCode || "USD";
  return `${currency} ${amount.toLocaleString("en", {
    minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    maximumFractionDigits: 2,
  })}`;
}

function getStatusLabel(status) {
  if (Number(status) === 1) return "Paid";
  return `Status ${status ?? "N/A"}`;
}

function getEnglishName(name) {
  if (!name || typeof name !== "object") return "N/A";
  const englishValue = name.en;
  if (typeof englishValue === "string") return englishValue || "N/A";
  if (!englishValue || typeof englishValue !== "object") return "N/A";
  return (
    englishValue.value || englishValue.message || englishValue.text || "N/A"
  );
}

function getDisplayEmail(user = {}) {
  const email = String(user?.email || "");
  const alternativeEmail = String(user?.emailAlternative || "").trim();
  if (email.toLowerCase().endsWith("@privaterelay.appleid.com") && alternativeEmail) {
    return alternativeEmail;
  }
  return email;
}

function getUserName(user) {
  if (!user) return "N/A";
  return (
    user.name ||
    `${user.firstName || ""} ${user.lastName || ""}`.trim() ||
    getDisplayEmail(user) ||
    "N/A"
  );
}

function CmsPurchasesPage() {
  const [search, setSearch] = useState("");
  const [environment, setEnvironment] = useState("all");
  const [page, setPage] = useState(1);
  const [purchaseModalView, setPurchaseModalView] = useState(null);
  const {
    purchases,
    pagination,
    selectedPurchase,
    loading,
    detailLoading,
    error,
    fetchPurchases,
    fetchPurchaseDetail,
    clearSelectedPurchase,
  } = useCmsPurchases();

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchPurchases({ page, limit: pageLimit, search, environment });
    }, 350);
    return () => clearTimeout(timer);
  }, [environment, fetchPurchases, page, search]);

  const canGoPrevious = pagination.page > 1;
  const canGoNext = pagination.page < pagination.totalPages;
  const rows = useMemo(() => purchases, [purchases]);
  const promoCode = selectedPurchase?.metadata?.promoCode;
  const purchaseItems = selectedPurchase?.items || [];
  const inclusiveAccessType = selectedPurchase?.metadata?.inclusiveAccessType;
  const metadataLog = selectedPurchase?.metadata
    ? JSON.stringify(selectedPurchase.metadata, null, 2)
    : "";

  const openPurchaseModal = (purchaseId, view) => {
    clearSelectedPurchase();
    setPurchaseModalView(view);
    fetchPurchaseDetail(purchaseId);
  };

  const closePurchaseModal = () => {
    setPurchaseModalView(null);
    clearSelectedPurchase();
  };

  return (
    <CmsLayout activeModule="purchases" title="Purchases">
      <section className="cms-purchases-page">
        <article className="panel table-panel cms-purchases-panel">
          <div className="cms-purchases-head">
            <div>
              <p className="cms-purchases-kicker">System purchases</p>
              <h2>Purchases</h2>
            </div>
            <div className="cms-purchases-filters">
              <label className="cms-purchases-environment-filter">
                <select
                  value={environment}
                  onChange={(event) => {
                    setEnvironment(event.target.value);
                    setPage(1);
                  }}
                  aria-label="Filter purchases by environment"
                >
                  <option value="all">ALL</option>
                  <option value="production">PRODUCTION</option>
                  <option value="sandbox">SANDBOX</option>
                </select>
              </label>
              <div className="cms-purchases-search">
                <IconSearch size={18} />
                <input
                  value={search}
                  onChange={(event) => {
                    setSearch(event.target.value);
                    setPage(1);
                  }}
                  placeholder="Search by reference, store, user or transaction"
                />
              </div>
            </div>
          </div>

          {error ? (
            <p className="message-error cms-purchases-message">{error}</p>
          ) : null}
          {loading ? (
            <p className="cms-purchases-loading">Loading purchases...</p>
          ) : null}

          <div className="table-wrap cms-purchases-table-wrap">
            <table className="data-table cms-purchases-table">
              <thead>
                <tr>
                  {/*  <th>Purchase</th> */}
                  <th>User</th>
                  <th>Store</th>
                  <th>Total</th>
                  <th>Status</th>
                  <th>Paid at</th>
                  <th>Complete</th>
                  {/* <th>Actions</th> */}
                </tr>
              </thead>
              <tbody>
                {rows.map((purchase) => {
                  const lastCompletedItem =
                    purchase.items
                      ?.filter((item) => item?.completed === true)
                      .at(-1) || null;

                  const purchaseItem = lastCompletedItem
                    ? {
                        startPoint: lastCompletedItem.startPoint || "",
                        tour: lastCompletedItem.tour || "",
                        name: lastCompletedItem.startPoint?.name?.en || "",
                      }
                    : null;

                  return (
                    <tr
                      key={purchase.id}
                      onClick={() => openPurchaseModal(purchase.id, "detail")}
                      style={{ cursor: "pointer" }}
                    >
                      {/*   <td>
                      <div className="cms-purchase-cell">
                        <span className="cms-purchase-avatar">
                          <IconReceipt size={21} />
                        </span>
                        <div>
                          <strong>{purchase.referenceCode || purchase.id}</strong>
                          <small>Transaction ID: {purchase.storeTransactionId || "N/A"}</small>
                        </div>
                      </div>
                    </td> */}
                      <td>
                        <div className="cms-purchase-user">
                          <strong>{getUserName(purchase.user)}</strong>
                          <small>{getDisplayEmail(purchase.user) || "No email"}</small>
                        </div>
                      </td>
                      <td>
                        <span className="cms-purchases-store">
                          {purchase.store || "N/A"}
                          {purchase.coupon ? ` - ${purchase.coupon}` : ""}
                          {Number(purchase.couponPercentage || 0) > 0
                            ? ` (${purchase.couponPercentage}%)`
                            : ""}
                        </span>
                      </td>
                      <td>{formatMoney(purchase)}</td>
                      <td>
                        <span className="cms-purchases-status">
                          {getStatusLabel(purchase.status)}
                        </span>
                      </td>
                      <td>
                        {formatBackendDate(purchase.paidAt || purchase.createdAt)}
                      </td>
                      <td>
                        <button
                          type="button"
                          className="cms-purchases-complete-btn"
                          onClick={() =>
                            openPurchaseModal(purchase.id, "completion")
                          }
                          title="View item completion"
                          aria-label="View item completion"
                        >
                          {purchaseItem?.name || <IconRocketOff size={22} />}
                        </button>
                      </td>
                      {/*   <td>
                        <button
                          type="button"
                          className="app-icon-btn app-icon-btn--update"
                          onClick={() =>
                            openPurchaseModal(purchase.id, "detail")
                          }
                          title="View purchase detail"
                          aria-label="View purchase detail"
                        >
                          <IconEye size={17} />
                        </button>
                      </td> */}
                    </tr>
                  );
                })}
                {!rows.length && !loading ? (
                  <tr>
                    <td colSpan={8}>No purchases found.</td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>

          <div className="cms-purchases-pagination">
            <button
              className="button-ghost"
              type="button"
              disabled={!canGoPrevious || loading}
              onClick={() => setPage((prev) => Math.max(1, prev - 1))}
            >
              <IconChevronLeft size={16} />
              Previous
            </button>
            <span>
              Page {pagination.page} of {pagination.totalPages} ·{" "}
              {pagination.total} purchases
            </span>
            <button
              className="button-ghost"
              type="button"
              disabled={!canGoNext || loading}
              onClick={() =>
                setPage((prev) => Math.min(pagination.totalPages, prev + 1))
              }
            >
              Next
              <IconChevronRight size={16} />
            </button>
          </div>
        </article>

        {purchaseModalView === "detail" &&
        (selectedPurchase || detailLoading) ? (
          <div className="app-modal-overlay" role="presentation">
            <article
              className="app-modal app-modal-xl cms-purchases-detail-modal"
              role="dialog"
              aria-modal="true"
            >
              <div className="app-modal-header cms-purchases-modal-head">
                <div>
                  <p className="cms-purchases-kicker">Purchase detail</p>
                  <h3>
                    {selectedPurchase?.referenceCode ||
                      selectedPurchase?.id ||
                      "Loading..."}
                  </h3>
                </div>
                <button
                  className="app-icon-btn app-icon-btn--back"
                  type="button"
                  onClick={closePurchaseModal}
                  aria-label="Close purchase detail"
                >
                  <IconX size={18} />
                </button>
              </div>

              <div className="app-modal-body cms-purchases-detail">
                {detailLoading ? (
                  <p className="cms-purchases-loading">Loading detail...</p>
                ) : null}

                {selectedPurchase && !detailLoading ? (
                  <>
                    <div className="cms-purchases-detail-grid">
                      <div>
                        <span>Reference</span>
                        <strong>
                          {selectedPurchase.referenceCode || "N/A"}
                        </strong>
                      </div>
                      <div>
                        <span>Transaction ID</span>
                        <strong>
                          {selectedPurchase.storeTransactionId || "N/A"}
                        </strong>
                      </div>
                      <div>
                        <span>Total</span>
                        <strong>{formatMoney(selectedPurchase)}</strong>
                      </div>
                      <div>
                        <span>Paid at</span>
                        <strong>{selectedPurchase.paidAt || "N/A"}</strong>
                      </div>
                      <div>
                        <span>Inclusive access type</span>
                        {typeof inclusiveAccessType === "boolean" ? (
                          <strong
                            className={`cms-purchases-boolean-icon ${inclusiveAccessType ? "is-true" : "is-false"}`}
                            title={inclusiveAccessType ? "True" : "False"}
                          >
                            {inclusiveAccessType ? (
                              <IconCircleCheck size={22} />
                            ) : (
                              <IconCircleX size={22} />
                            )}
                          </strong>
                        ) : (
                          <strong>N/A</strong>
                        )}
                      </div>
                    </div>

                    <div className="cms-purchases-detail-section">
                      <h4>
                        <IconUserCircle size={18} />
                        User
                      </h4>
                      <div className="cms-purchases-user-card">
                        <strong>{getUserName(selectedPurchase.user)}</strong>
                        <span>
                          {getDisplayEmail(selectedPurchase.user) || "No email"}
                        </span>
                        <small>
                          Alternative email:{" "}
                          {selectedPurchase.user?.emailAlternative || "N/A"}
                        </small>
                        <small>
                          Firebase ID:{" "}
                          {selectedPurchase.user?.firebaseId || "N/A"}
                        </small>
                        {selectedPurchase.user?.phone ? (
                          <small>Phone: {selectedPurchase.user.phone}</small>
                        ) : null}
                      </div>
                    </div>

                    {promoCode ? (
                      <div className="cms-purchases-detail-section">
                        <h4>Promo code</h4>
                        <div className="cms-purchases-promo-card">
                          <strong>{promoCode.code || "N/A"}</strong>
                          <span>{promoCode.percentage ?? 0}% discount</span>
                        </div>
                      </div>
                    ) : null}

                    <div className="cms-purchases-detail-section">
                      <h4>
                        <IconShoppingCart size={18} />
                        Items ({purchaseItems.length})
                      </h4>
                      <div className="cms-purchases-item-list">
                        {purchaseItems.map((item) => (
                          <article
                            key={item.id}
                            className="cms-purchases-item-card cms-purchases-item-card--detail"
                          >
                            <div className="cms-purchases-start-point-info">
                              <img
                                src={item.startPoint?.imageUrl || ""}
                                alt=""
                                onError={(event) => {
                                  event.currentTarget.style.display = "none";
                                }}
                              />
                              <div>
                                <strong>
                                  {getEnglishName(item.startPoint?.name)}
                                </strong>
                              </div>
                            </div>
                            <div className="cms-purchases-tour-info">
                              <span>Tour</span>
                              <strong>{getEnglishName(item.tour?.name)}</strong>
                            </div>
                          </article>
                        ))}
                        {!purchaseItems.length ? (
                          <p className="cms-purchases-muted">No items found.</p>
                        ) : null}
                      </div>
                    </div>

                    <div className="cms-purchases-detail-section">
                      <h4>Metadata log</h4>
                      <pre className="cms-purchases-metadata-log">
                        {metadataLog || "{}"}
                      </pre>
                    </div>
                  </>
                ) : null}
              </div>
            </article>
          </div>
        ) : null}

        {purchaseModalView === "completion" &&
        (selectedPurchase || detailLoading) ? (
          <PurchaseCompletionModal
            purchase={selectedPurchase}
            loading={detailLoading}
            onClose={closePurchaseModal}
            resolveName={getEnglishName}
          />
        ) : null}
      </section>
    </CmsLayout>
  );
}

export default CmsPurchasesPage;
